import React from 'react';
import { Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { LANGUAGE_PICKER_OPTIONS } from '../../utils/languages';
import css from './LanguagePicker.module.scss';

const AUTO_HINT =
  "Auto: we write in the job's language when your CV shows you can — otherwise English. " +
  'Pick a language to override.';

interface LanguagePickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  ariaLabel?: string;
  /**
   * Show the info icon explaining the "Auto" behavior (default true). Turn off
   * on surfaces that already carry an always-visible hint with the same copy.
   */
  showAutoHint?: boolean;
}

/**
 * Shared "Auto + curated languages" picker used everywhere the user can choose a
 * draft language (search creation, cover-letter regenerate, reply rewrite).
 * Searchable so all 16 named languages stay reachable without a long scroll.
 */
const LanguagePicker: React.FC<LanguagePickerProps> = ({
  value,
  onChange,
  disabled,
  size = 'middle',
  className,
  ariaLabel = 'Draft language',
  showAutoHint = true,
}) => (
  <>
    <Select
      className={className ? `${css.picker} ${className}` : css.picker}
      value={value}
      onChange={onChange}
      options={LANGUAGE_PICKER_OPTIONS}
      disabled={disabled}
      size={size}
      showSearch
      optionFilterProp="label"
      aria-label={ariaLabel}
      popupMatchSelectWidth={false}
    />
    {showAutoHint && (
      <Tooltip title={AUTO_HINT} trigger={['hover', 'focus', 'click']}>
        <button
          type="button"
          className={css.infoButton}
          aria-label="How the draft language is chosen"
        >
          <InfoCircleOutlined aria-hidden />
        </button>
      </Tooltip>
    )}
  </>
);

export default LanguagePicker;
